import { useEffect } from "react";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { gql } from "@/services/httpAdapter";
import { useQueryClient } from "@tanstack/react-query";
import {
	commissionCycleSubscriptionCb,
	updateWashoutModelCb,
	updateWashoutReviewTreeCb,
} from "@/Washouts/helpers/WashoutReview/subscriptionCallbacks";

type TUseApproveWashout = {
	locationId?: string;
	commissionCycleId?: string;
	payPeriodId?: string;
	updateWashoutReviewTree?: boolean;
	updateCommissionCycle?: boolean;
	updateWashoutStatus?: boolean;
	updateWashoutModel?: boolean;
};

type TResult = {
	position: string;
	status: string;
	userId: string;
	review: { status: string };
	employee: { _id: string; position: string };
};

const useApproveRetractWashoutSubscription = (params: TUseApproveWashout) => {
	const {
		locationId = "",
		commissionCycleId = "",
		payPeriodId = "",
		updateCommissionCycle = false,
		updateWashoutReviewTree = false,
		updateWashoutModel = false,
	} = params;
	const queryClient = useQueryClient();
	const { addSubscription, getSubscription } = useSubscriptions();

	const updateWashoutStatusSubscription = getSubscription(["updateWashoutStatus", commissionCycleId, payPeriodId]);

	updateWashoutStatusSubscription?.subscriptions?.onUpdateAllWashoutsStatus?.setCallback(
		(result: { data: { onUpdateAllWashoutsStatus: TResult } }) => {
			if (updateWashoutReviewTree) {
				updateWashoutReviewTreeCb({ queryClient, locationId, payPeriodId, result: result.data.onUpdateAllWashoutsStatus });
			}
			if (updateCommissionCycle) {
				commissionCycleSubscriptionCb({
					queryClient,
					locationId,
					payPeriodId,
					result: [result.data.onUpdateAllWashoutsStatus],
				});
			}
			if (updateWashoutModel) {
				updateWashoutModelCb({ queryClient, commissionCycleId, payPeriodId, result: result.data.onUpdateAllWashoutsStatus });
			}
		}
	);

	useEffect(() => {
		if (locationId && commissionCycleId && payPeriodId) {
			addSubscription(
				["updateWashoutStatus", commissionCycleId, payPeriodId],
				[
					{
						context: "onUpdateAllWashoutsStatus",
						query: gql`
							subscription onUpdateAllWashoutsStatus($commissionCycleId: String, $payPeriodId: String) {
								onUpdateAllWashoutsStatus(commissionCycleId: $commissionCycleId, payPeriodId: $payPeriodId) {
									_id
									commissionCycleId
									payPeriodId
									userId
									position
									status
									employee {
										_id
										position
									}
									review {
										status
										statusSetBy {
											_id
											fullName
										}
									}
								}
							}
						`,
						variables: {
							commissionCycleId,
							payPeriodId,
						},
					},
				]
			);
		}
	}, [locationId, commissionCycleId, payPeriodId, addSubscription]);
};

export default useApproveRetractWashoutSubscription;
